import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function Contact() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  const budgetKeys = [
    'contact.budget.1',
    'contact.budget.2',
    'contact.budget.3',
    'contact.budget.4',
    'contact.budget.5',
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const msg = form.querySelector<HTMLTextAreaElement>('textarea[name="mensaje"]')?.value || '';
    if (msg.length > 500) return;

    setLoading(true);

    const formData = new FormData(form);
    const nombre = (formData.get('nombre') || '').toString();
    const telefono = (formData.get('telefono') || '').toString();
    const email = (formData.get('email') || '').toString();
    const modelo = (formData.get('modelo') || '').toString();
    const presupuesto = (formData.get('presupuesto') || '').toString();
    const mensaje = (formData.get('mensaje') || '').toString();

    const destinatario = 'z.s.carsimport@gmail.com';
    const asunto = `Nueva consulta web - ${nombre}`;
    const cuerpo =
      `Nombre: ${nombre}\n` +
      `Teléfono: ${telefono}\n` +
      `Email: ${email}\n` +
      `Modelo de interés: ${modelo}\n` +
      `Presupuesto: ${presupuesto || 'No indicado'}\n\n` +
      `Mensaje:\n${mensaje}`;

    const mailtoUrl =
      `mailto:${destinatario}` +
      `?subject=${encodeURIComponent(asunto)}` +
      `&body=${encodeURIComponent(cuerpo)}`;

    window.location.href = mailtoUrl;

    setSubmitted(true);
    setLoading(false);
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/34631611077?text=Hola%2C%20me%20gustar%C3%ADa%20obtener%20informaci%C3%B3n%20sobre%20importaci%C3%B3n%20de%20veh%C3%ADculos.', '_blank');
  };

  const contactItems = [
    { icon: 'ri-whatsapp-line', labelKey: 'contact.whatsapp', valueKey: 'contact.whatsapp.value', value: '+34 631 611 077', action: handleWhatsApp },
    { icon: 'ri-mail-line', labelKey: 'contact.email', valueKey: 'contact.email.value', value: 'z.s.carsimport@gmail.com', action: () => window.open('mailto:z.s.carsimport@gmail.com', '_blank') },
    { icon: 'ri-map-pin-line', labelKey: 'contact.location', valueKey: 'contact.location.value', value: t('contact.location.value'), action: () => {} },
    { icon: 'ri-time-line', labelKey: 'contact.schedule', valueKey: 'contact.schedule.value', value: t('contact.schedule.value'), action: () => {} },
  ];

  return (
    <section id="contacto" ref={sectionRef} className="py-16 sm:py-28 bg-[#050505] relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-purple-900/10 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left Info */}
          <div className="scroll-reveal">
            <div className="inline-flex items-center gap-2 border border-purple-500/30 px-4 py-2 rounded-full mb-8">
              <span className="text-purple-400 text-xs tracking-widest uppercase font-medium">{t('contact.label')}</span>
            </div>
            <h2 className="font-orbitron text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              {t('contact.title1')}{' '}
              <span className="gradient-text-purple">{t('contact.title2')}</span>
            </h2>
            <p className="text-white/50 text-lg leading-relaxed mb-10">
              {t('contact.subtitle')}
            </p>

            {/* Contact Info */}
            <div className="flex flex-col gap-4 mb-10">
              {contactItems.map((item) => (
                <div
                  key={item.labelKey}
                  onClick={item.action}
                  className="flex items-center gap-4 glass-card rounded-xl p-4 hover:border-purple-500/30 transition-all duration-300 cursor-pointer group"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-purple-900/30 border border-purple-500/20 group-hover:bg-purple-600/30 transition-all duration-300 flex-shrink-0">
                    <i className={`${item.icon} text-purple-400`} />
                  </div>
                  <div>
                    <div className="text-white/40 text-xs tracking-widest uppercase">{t(item.labelKey)}</div>
                    <div className="text-white text-sm font-medium">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* WhatsApp CTA */}
            <button
              onClick={handleWhatsApp}
              className="flex items-center gap-3 bg-green-600 hover:bg-green-500 text-white font-semibold tracking-wider uppercase px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-xl" />
              {t('contact.whatsapp.cta')}
            </button>
          </div>

          {/* Right Form */}
          <div className="scroll-reveal" style={{ transitionDelay: '200ms' }}>
            <div className="glass-card rounded-2xl p-5 sm:p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 flex items-center justify-center rounded-full gradient-purple mx-auto mb-6 glow-purple">
                    <i className="ri-check-line text-white text-3xl" />
                  </div>
                  <h3 className="font-orbitron text-2xl font-bold text-white mb-3">{t('contact.success.title')}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">
                    {t('contact.success.desc')}
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-6 gradient-purple text-white text-sm font-semibold px-6 py-3 rounded-full cursor-pointer whitespace-nowrap"
                  >
                    {t('contact.form.new')}
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="zscars-contact-form"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <h3 className="font-rajdhani text-2xl font-bold text-white tracking-wide mb-2">
                    {t('contact.form.title')}
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-white/40 text-xs tracking-widest uppercase mb-2 block">{t('contact.form.name')} *</label>
                      <input
                        type="text"
                        name="nombre"
                        required
                        placeholder={t('contact.form.name.ph')}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/50 transition-colors duration-300"
                      />
                    </div>
                    <div>
                      <label className="text-white/40 text-xs tracking-widest uppercase mb-2 block">{t('contact.form.phone')} *</label>
                      <input
                        type="tel"
                        name="telefono"
                        required
                        placeholder={t('contact.form.phone.ph')}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/50 transition-colors duration-300"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-white/40 text-xs tracking-widest uppercase mb-2 block">{t('contact.form.email')} *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder={t('contact.form.email.ph')}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/50 transition-colors duration-300"
                    />
                  </div>

                  <div>
                    <label className="text-white/40 text-xs tracking-widest uppercase mb-2 block">{t('contact.form.model')} *</label>
                    <input
                      type="text"
                      name="modelo"
                      required
                      placeholder={t('contact.form.model.ph')}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/50 transition-colors duration-300"
                    />
                  </div>

                  <div>
                    <label className="text-white/40 text-xs tracking-widest uppercase mb-2 block">{t('contact.form.budget')}</label>
                    <select
                      name="presupuesto"
                      className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-purple-500/50 transition-colors duration-300 cursor-pointer"
                    >
                      <option value="">{t('contact.form.budget.ph')}</option>
                      {budgetKeys.map((key) => (
                        <option key={key} value={t(key)}>{t(key)}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-white/40 text-xs tracking-widest uppercase mb-2 block">
                      {t('contact.form.message')} <span className={charCount > 500 ? 'text-red-400' : 'text-white/30'}>({charCount}/500)</span>
                    </label>
                    <textarea
                      name="mensaje"
                      rows={4}
                      maxLength={500}
                      placeholder={t('contact.form.message.ph')}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/50 transition-colors duration-300 resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading || charCount > 500}
                    className="gradient-purple text-white font-semibold tracking-wider uppercase py-4 rounded-xl glow-purple hover:scale-[1.02] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin" />
                        {t('contact.form.sending')}
                      </span>
                    ) : (
                      <>
                        <i className="ri-send-plane-line mr-2" />
                        {t('contact.form.submit')}
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}